import { WBEnvelope } from '@/domain/models/WBProfileAirplane'

import { isSet } from '@/utils/validation'

export interface EnumeratedEnvelopeLimit {
  limit: WBEnvelope
  identifierNumber: number
}

/**
 * Como o eixo de braço do limite é lido. O perfil guarda cada limite em dupla representação
 * (`arm` e `macPercent`) e o gráfico do preenchimento pode estar em qualquer uma das duas — a
 * ordem tem de ser calculada no MESMO eixo em que o polígono vai ser desenhado, senão um envelope
 * gravado só em %CMA ordena por braço nulo.
 */
type ArmAccessor = (limit: WBEnvelope) => number

export interface EnvelopeOrderOptions {
  getArm?: ArmAccessor
  /**
   * Descarta a sequência gravada e refaz o contorno pela varredura. Serve para quando o contorno
   * deixou de valer: um vértice novo não tem posição no ciclo, e manter os números dos outros
   * pontos daria uma figura com um ponto pendurado.
   */
  ignoreStoredSequence?: boolean
}

interface IndexedEnvelopeLimit {
  limit: WBEnvelope
  originalIndex: number
  arm: number
}

const defaultArmAccessor: ArmAccessor = (limit) => limit.arm

const compareIds = (a: WBEnvelope, b: WBEnvelope) => {
  return a.id.toString().localeCompare(b.id.toString())
}

const compareByAnchorPosition = (a: IndexedEnvelopeLimit, b: IndexedEnvelopeLimit) => {
  if (a.arm !== b.arm) return a.arm - b.arm
  if (a.limit.weight !== b.limit.weight) return a.limit.weight - b.limit.weight
  const idComparison = compareIds(a.limit, b.limit)
  if (idComparison !== 0) return idComparison
  return a.originalIndex - b.originalIndex
}

const compareByClockwiseAngle = (a: IndexedEnvelopeLimit, b: IndexedEnvelopeLimit, cx: number, cy: number) => {
  const angleA = Math.atan2(a.limit.weight - cy, a.arm - cx)
  const angleB = Math.atan2(b.limit.weight - cy, b.arm - cx)

  if (angleA !== angleB) return angleB - angleA

  return compareByAnchorPosition(a, b)
}

const isPlottable = (indexed: IndexedEnvelopeLimit) => {
  return Number.isFinite(indexed.arm) && Number.isFinite(indexed.limit.weight)
}

/**
 * A sequência do contorno veio inteira?
 *
 * A decisão é do envelope, nunca do ponto: completar ponto a ponto o que faltou mistura a sequência
 * publicada com números inventados, e o resultado é um contorno que ninguém escreveu. Pior, um
 * conjunto assim completado satisfaz esta mesma checagem no `orderEnvelopeLimits` e passa a valer
 * como autoridade, sem nunca cair na varredura angular — o erro fica silencioso.
 *
 * Aceita qualquer coisa com `pointSequence` para servir também à borda do wire, onde os pontos ainda
 * são DTO e não `WBEnvelope`.
 */
export function hasCompleteEnvelopeSequence(points: { pointSequence?: number }[]): boolean {
  return !!points?.length && points.every((point) => isSet(point.pointSequence))
}

/**
 * Varredura angular em torno do centroide, com âncora determinística no ponto de menor braço.
 *
 * Reproduz o ciclo publicado em 53 de 53 modelos do catálogo e em 30 de 30 perfis reais medidos
 * (inclusive os côncavos), o que é a razão de ela servir de recuo para a linha antiga que voltar do
 * servidor sem `pointSequence`. Não é infalível: só acerta polígono estrelado em relação ao
 * centroide, e é por isso que a sequência gravada tem precedência sempre que existir.
 */
const orderByClockwiseSweep = (indexedLimits: IndexedEnvelopeLimit[]): IndexedEnvelopeLimit[] => {
  const cx = indexedLimits.reduce((sum, indexed) => sum + indexed.arm, 0) / indexedLimits.length
  const cy = indexedLimits.reduce((sum, indexed) => sum + indexed.limit.weight, 0) / indexedLimits.length

  const clockwiseLimits = [...indexedLimits].sort((a, b) => compareByClockwiseAngle(a, b, cx, cy))
  const anchor = [...clockwiseLimits].sort(compareByAnchorPosition)[0]
  const startIndex = clockwiseLimits.findIndex(({ originalIndex }) => originalIndex === anchor.originalIndex)

  return clockwiseLimits.slice(startIndex).concat(clockwiseLimits.slice(0, startIndex))
}

/**
 * Ordem canônica do envelope: a sequência do contorno, não a ordem em que os pontos chegaram.
 *
 * Polígono é sequência — os mesmos pontos em outra ordem são outra figura, e essa figura é o
 * veredito de CG, não só o desenho. A ordem que o servidor devolve NÃO é o contorno (as rotas
 * ordenam por peso, e em 0 de 30 perfis reais isso fecha um polígono simples: dá figura
 * autointersectante), então quem monta o polígono precisa reordenar antes.
 *
 * Precedência:
 * 1. `pointSequence`, quando todo ponto tem o campo — é o contorno gravado, a autoridade.
 * 2. varredura angular, para a linha antiga que ainda voltar com o campo nulo.
 *
 * Pontos sem coordenada utilizável (braço nulo no eixo escolhido) não participam da varredura e vão
 * para o fim, preservando a ordem de entrada: eles seriam NaN no `atan2` e embaralhariam o resto.
 */
export function orderEnvelopeLimits(limits: WBEnvelope[], options: EnvelopeOrderOptions = {}): WBEnvelope[] {
  const { getArm = defaultArmAccessor, ignoreStoredSequence = false } = options

  if (!limits?.length) return []
  if (limits.length === 1) return [...limits]

  const indexedLimits: IndexedEnvelopeLimit[] = limits.map((limit, originalIndex) => ({
    limit,
    originalIndex,
    arm: getArm(limit)
  }))

  if (!ignoreStoredSequence && hasCompleteEnvelopeSequence(limits)) {
    return [...indexedLimits]
      .sort((a, b) => a.limit.pointSequence - b.limit.pointSequence || a.originalIndex - b.originalIndex)
      .map(({ limit }) => limit)
  }

  const plottable = indexedLimits.filter(isPlottable)
  const rest = indexedLimits.filter((indexed) => !isPlottable(indexed))

  // Com dois pontos não há contorno para varrer, só uma ordem estável.
  const ordered =
    plottable.length <= 2 ? [...plottable].sort(compareByAnchorPosition) : orderByClockwiseSweep(plottable)

  return ordered.concat(rest).map(({ limit }) => limit)
}

/**
 * Grava a ordem canônica nos próprios pontos, numerando de 1 a N.
 *
 * É o que faz cada perfil convergir para o campo com o uso: a linha antiga volta sem
 * `pointSequence`, é reconstruída pela varredura e sai da próxima gravação com o contorno
 * persistido. A numeração é a mesma que a tela da aeronave mostra na lista de limites.
 */
export function renumberEnvelopeLimits(limits: WBEnvelope[], options: EnvelopeOrderOptions = {}): WBEnvelope[] {
  return orderEnvelopeLimits(limits, options).map((limit, index) => ({ ...limit, pointSequence: index + 1 }))
}

/**
 * Refaz o contorno do zero pela varredura, ignorando a sequência gravada.
 */
export function rebuildEnvelopeSequence(limits: WBEnvelope[], options: EnvelopeOrderOptions = {}): WBEnvelope[] {
  return renumberEnvelopeLimits(limits, { ...options, ignoreStoredSequence: true })
}

export function enumerateEnvelopeLimitsForDisplay(limits: WBEnvelope[]): EnumeratedEnvelopeLimit[] {
  return orderEnvelopeLimits(limits).map((limit, index) => ({
    limit,
    identifierNumber: index + 1
  }))
}
